import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle2, 
  Server, 
  Shield, 
  Phone, 
  Mail, 
  Globe,
  MonitorSmartphone 
} from "lucide-react";
import { useAbmConnections } from "@/hooks/useAbmConnections";
import { useAdeDevices } from "@/hooks/useAdeDevices";

interface ReviewStepProps {
  formData: any;
  selectedDevices: string[];
}

export const ReviewStep = ({ formData, selectedDevices }: ReviewStepProps) => {
  const { connections } = useAbmConnections();
  const { devices } = useAdeDevices();

  const selectedConnection = connections.find(c => c.id === formData.abm_connection_id);
  const selectedDeviceObjects = devices.filter(d => selectedDevices.includes(d.id));

  const skipItems = formData.skip_setup_items || [];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Review & Create</h2>
        <p className="text-muted-foreground">
          Review your enrollment profile configuration before deploying
        </p>
      </div>

      <div className="space-y-4">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Profile Name</p>
                <p className="font-medium">{formData.profile_name}</p>
              </div>
              {formData.department && (
                <div>
                  <p className="text-sm text-muted-foreground">Department</p>
                  <p className="font-medium">{formData.department}</p>
                </div>
              )}
            </div>
            {formData.description && (
              <div>
                <p className="text-sm text-muted-foreground">Description</p>
                <p className="text-sm">{formData.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ABM Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" />
              ABM Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedConnection ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{selectedConnection.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Organization: {selectedConnection.organization_name || 'N/A'}
                  </p>
                </div>
                <Badge variant="outline">Active</Badge>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No connection selected</p>
            )}
          </CardContent>
        </Card>

        {/* Setup Assistant */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Setup Assistant Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">Mandatory Enrollment</span>
                <Badge variant={formData.is_mandatory ? "default" : "secondary"}>
                  {formData.is_mandatory ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">MDM Removable</span>
                <Badge variant={formData.is_mdm_removable ? "default" : "secondary"}>
                  {formData.is_mdm_removable ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">Wait for Configuration</span>
                <Badge variant={formData.await_device_configured ? "default" : "secondary"}>
                  {formData.await_device_configured ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">Auto-Advance Setup</span>
                <Badge variant={formData.auto_advance_setup ? "default" : "secondary"}>
                  {formData.auto_advance_setup ? "Yes" : "No"}
                </Badge>
              </div>
            </div>

            {skipItems.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Skipped Setup Screens ({skipItems.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {skipItems.map((item: string) => (
                    <Badge key={item} variant="outline">
                      {item}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Support Info */}
        {(formData.support_phone_number || formData.support_email_address || formData.configuration_web_url) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Support Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {formData.support_phone_number && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{formData.support_phone_number}</span>
                </div>
              )}
              {formData.support_email_address && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{formData.support_email_address}</span>
                </div>
              )}
              {formData.configuration_web_url && (
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{formData.configuration_web_url}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Device Assignment */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MonitorSmartphone className="h-5 w-5 text-primary" />
              Device Assignment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Devices Selected</span>
                <Badge variant="secondary">{selectedDevices.length} devices</Badge>
              </div>
              {selectedDeviceObjects.length > 0 && (
                <div className="space-y-2">
                  {selectedDeviceObjects.slice(0, 5).map((device) => (
                    <div key={device.id} className="text-sm p-2 bg-muted/50 rounded">
                      <span className="font-mono">{device.serial_number}</span>
                      <span className="text-muted-foreground ml-2">• {device.model}</span>
                    </div>
                  ))}
                  {selectedDeviceObjects.length > 5 && (
                    <p className="text-sm text-muted-foreground">
                      And {selectedDeviceObjects.length - 5} more devices...
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
